import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { templateCategoryService } from "../api/services/templateCategoryService";
import {
  TemplateCategoryFilters,
  TemplateCategoryResult,
  UpdateTemplateCategoryPayload,
} from "../schemas/templateCategorySchema";

export const useTemplateCategories = (
  filters: TemplateCategoryFilters = {},
  options?: { enabled?: boolean },
) => {
  const queryClient = useQueryClient();

  const {
    data: templateCategories,
    isLoading: isTemplateCategoriesLoading,
    refetch,
  } = useQuery<TemplateCategoryResult, Error>({
    queryKey: ["templateCategories", filters],
    queryFn: () => templateCategoryService.getAll(filters),
    enabled: options?.enabled !== false,
  });

  const createTemplateCategoryMutation = useMutation({
    mutationFn: templateCategoryService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templateCategories"] });
      queryClient.invalidateQueries({ queryKey: ["templateCategoryOptions"] });
      toast.success("Template Category created successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create Template Category");
    },
  });

  const updateTemplateCategoryMutation = useMutation({
    mutationFn: templateCategoryService.update,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templateCategories"] });
      queryClient.invalidateQueries({ queryKey: ["templateCategoryOptions"] });
      toast.success("Template Category updated successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update Template Category");
    },
  });

  const deleteTemplateCategoryMutation = useMutation({
    mutationFn: templateCategoryService.remove,
    onSuccess: (_data, ids) => {
      queryClient.invalidateQueries({ queryKey: ["templateCategories"] });
      queryClient.invalidateQueries({ queryKey: ["templateCategoryOptions"] });
      if (Array.isArray(ids) && ids.length > 1) {
        toast.success(`${ids.length} items deleted successfully`);
        return;
      }
      toast.success("Template Category deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete Template Category");
    },
  });

  const deleteTemplateCategories = (ids: string[]) => {
    deleteTemplateCategoryMutation.mutate(ids);
  };

  return {
    templateCategories,
    refetchTemplateCategories: refetch,
    isLoading:
      isTemplateCategoriesLoading ||
      createTemplateCategoryMutation.isPending ||
      updateTemplateCategoryMutation.isPending ||
      deleteTemplateCategoryMutation.isPending,
    createTemplateCategory: createTemplateCategoryMutation.mutate,
    updateTemplateCategory: updateTemplateCategoryMutation.mutate,
    deleteTemplateCategories,
  };
};
